import * as React from "react";
import { useLocation, useParams, NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const LEADER_WORKSPACE_BREADCRUMBS: Record<string, string> = {
    review: "Review Work",
    throughline: "Coach Throughline",
    patterns: "Emerging Patterns",
    brief: "Next Session Brief",
    arc: "Leader Arc & Moves",
    record: "Leadership Record",
};

export function CoachBreadcrumbs() {
    const { leaderId } = useParams<{ leaderId: string }>();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const workspaceLabel = LEADER_WORKSPACE_BREADCRUMBS[searchParams.get("workspace") ?? ""];

    const { data: profile } = useQuery({
        queryKey: ["breadcrumb-leader", leaderId],
        queryFn: async () => {
            if (!leaderId) return null;
            const { data } = await supabase
                .from("profiles")
                .select("full_name")
                .eq("id", leaderId)
                .maybeSingle();
            return data;
        },
        enabled: !!leaderId,
    });

    const pathSegments = location.pathname.split("/").filter(Boolean);

    // Custom logic to map coach paths to breadcrumb items
    const items: Array<{ label: string; href?: string; isCurrent?: boolean }> = [];

    const isCoachingSystemPath =
        pathSegments.includes("identity") ||
        pathSegments.includes("philosophy") ||
        pathSegments.includes("my-voice");

    // Coach-system working pages belong to Coach Hub, not Overview.
    items.push(isCoachingSystemPath
        ? { label: "Coach Hub", href: "/coach/hub" }
        : { label: "Coach", href: "/coach/overview" });

    if (pathSegments.includes("leaders")) {
        if (pathSegments.length > 2) { // More than just /coach/leaders
            items.push({ label: "Leaders", href: "/coach/leaders" });

            if (leaderId) {
                const isSynthesis = location.pathname.includes("/synthesis/");
                const isReview = location.pathname.includes("/review");

                if (workspaceLabel) {
                    const leaderMapSearchParams = new URLSearchParams(location.search);
                    leaderMapSearchParams.delete("workspace");
                    const leaderMapSearch = leaderMapSearchParams.toString();
                    const leaderMapHref = `${location.pathname}${leaderMapSearch ? `?${leaderMapSearch}` : ""}`;

                    items.push({ label: profile?.full_name || "Leader", href: leaderMapHref });
                    items.push({ label: workspaceLabel, isCurrent: true });
                } else if (isSynthesis || isReview) {
                    items.push({ label: profile?.full_name || "Leader", href: `/coach/leaders/${leaderId}` });
                    items.push({ label: isSynthesis ? "Synthesis" : "Review", isCurrent: true });
                } else {
                    items.push({ label: profile?.full_name || "Leader", isCurrent: true });
                }
            }
        } else {
            items.push({ label: "Leaders", isCurrent: true });
        }
    } else if (pathSegments.includes("overview")) {
        // Already added Coach, and overview is the root, so maybe don't add more or just mark current
        items[0].isCurrent = true;
    } else if (pathSegments.includes("messages")) {
        items.push({ label: "Messages", isCurrent: true });
    } else if (pathSegments.includes("calendar") || pathSegments.includes("meetings") || pathSegments.includes("calls")) {
        items.push({ label: "Meetings", isCurrent: true });
    } else if (pathSegments.includes("reviews")) {
        items.push({ label: "Review Access", isCurrent: true });
    } else if (pathSegments.includes("hub") || pathSegments.includes("veera") || pathSegments.includes("ai")) {
        items.push({ label: "Coach Hub", isCurrent: true });
    } else if (isCoachingSystemPath) {
        items.push({ label: "My Coaching System", isCurrent: true });
    } else if (pathSegments.includes("settings")) {
        items.push({ label: "Settings", isCurrent: true });
    } else if (pathSegments.includes("help")) {
        items.push({ label: "Help", isCurrent: true });
    }

    if (items.length <= 1 && items[0]?.isCurrent) return null; // Don't show if only one item and it's current

    return (
        <Breadcrumb className="px-1 py-2">
            <BreadcrumbList>
                {items.map((item, index) => (
                    <React.Fragment key={index}>
                        <BreadcrumbItem>
                            {item.isCurrent || index === items.length - 1 ? (
                                <BreadcrumbPage className="font-medium">{item.label}</BreadcrumbPage>
                            ) : (
                                <BreadcrumbLink asChild>
                                    <NavLink to={item.href!} className="text-muted-foreground hover:text-foreground transition-colors">
                                        {item.label}
                                    </NavLink>
                                </BreadcrumbLink>
                            )}
                        </BreadcrumbItem>
                        {index < items.length - 1 && <BreadcrumbSeparator />}
                    </React.Fragment>
                ))}
            </BreadcrumbList>
        </Breadcrumb>
    );
}
